/**
 * @fileoverview Files Table Configuration
 *
 * Centralized configuration for the files/documents table.
 * Defines columns, sort logic, and picker configurations for file management.
 *
 * @module Files/TableConfig
 */

import {
  TableColumnsConfig,
  PickerConfig,
  TableSortConfig
} from 'shared/components/UniversalTable/types';

// ========== COLUMN CONFIGURATION ==========

/**
 * Files table column definitions
 *
 * Defines all columns in the files table with their:
 * - Width behavior (fixed vs flexible)
 * - Sort keys
 * - Responsive visibility
 */
export const FILES_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: 'checkbox',
      label: '', // Header renders the "select all on page" checkbox automatically
      minWidth: 44,
      defaultWeight: 0, // Fixed width
      className: 'flex justify-center',
      toggleable: false
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 50,
      defaultWeight: 0, // Fixed width
      sortKey: 'status',
      toggleable: false
    },
    {
      id: 'actions',
      label: 'Acties',
      minWidth: 110,
      defaultWeight: 0, // Fixed width
      className: 'flex justify-center',
      toggleable: false
    },
    {
      id: 'id',
      label: 'ID',
      minWidth: 70,
      defaultWeight: 0, // Fixed width
      sortKey: 'id'
    },
    {
      id: 'building',
      label: 'Gebouw',
      minWidth: 90,
      defaultWeight: 2, // Flexible
      sortKey: 'building'
    },
    {
      id: 'unit',
      label: 'Unit',
      minWidth: 90,
      defaultWeight: 2, // Flexible
      sortKey: 'unit'
    },
    {
      id: 'company',
      label: 'Vennootschap',
      minWidth: 120,
      defaultWeight: 2, // Flexible
    },
    {
      id: 'renovationProject',
      label: 'Renovatieproject',
      minWidth: 120,
      defaultWeight: 2, // Flexible
      sortKey: 'renovationProject',
      hiddenByDefault: true
    },
    {
      id: 'projectPart',
      label: 'Projectonderdeel',
      minWidth: 120,
      defaultWeight: 2, // Flexible
      sortKey: 'projectPart',
      hiddenByDefault: true
    },
    {
      id: 'labels',
      label: 'Labels',
      minWidth: 160,
      defaultWeight: 3, // Main content, takes more space
    },
    {
      id: 'documentDate',
      label: 'Documentdatum',
      minWidth: 100,
      defaultWeight: 0, // Fixed width
      sortKey: 'documentDate'
    },
    {
      id: 'expirationDate',
      label: 'Vervaldatum',
      minWidth: 100,
      defaultWeight: 0, // Fixed width
    },
    {
      id: 'summary',
      label: 'Samenvatting',
      minWidth: 150,
      defaultWeight: 3, // Flexible
    },
    {
      id: 'relations',
      label: 'Relaties',
      minWidth: 120,
      defaultWeight: 2, // Flexible
    },
  ],
  // v3: bumped after adding the leading selection-checkbox column so persisted
  // (positional) widths recompute instead of being misaligned by the new column.
  localStorageKey: 'files-table-column-widths-v3'
};

// ========== PICKER FIELD CONFIGURATION ==========

/**
 * Files picker field mappings
 *
 * Defines how cell clicks map to picker modals and how updates are processed.
 */
export const FILES_PICKER_CONFIG: PickerConfig = {
  entityName: 'bestand',
  entityPrefix: '',
  apiUpdateEndpoint: '/file/update-file-log/{id}',
  entityPlural: 'bestanden',

  fieldMappings: [
    // Status field
    {
      eventMode: 'status',
      pickerMode: 'status',
      fieldName: 'status',
      titleTemplate: 'Selecteer status voor {count} {entityName}',
      extractCurrentValue: (row) => row.status,
      buildUpdatePayload: (val) => ({ status: val })
    },

    // Building field
    {
      eventMode: 'building',
      pickerMode: 'building',
      fieldName: 'building',
      titleTemplate: 'Selecteer gebouw voor {count} {entityName}',
      extractCurrentValue: (row) => row.building,
      buildUpdatePayload: (val) => ({ building: val ? { id: val.id } : null })
    },

    // Unit field
    {
      eventMode: 'unit',
      pickerMode: 'unit',
      fieldName: 'unit',
      titleTemplate: 'Selecteer unit voor {count} {entityName}',
      extractCurrentValue: (row) => row.unit,
      extractContextValue: (row) => row.building?.id,
      buildUpdatePayload: (val) => ({ unit: val ? { id: val.id } : null })
    },

    // Renovation Project field
    {
      eventMode: 'renovation-project',
      pickerMode: 'renovation-project',
      fieldName: 'renovationProject',
      titleTemplate: 'Selecteer renovatieproject voor {count} {entityName}',
      extractCurrentValue: (row) => row.renovationProject,
      extractContextValue: (row) => row.building?.id,
      buildUpdatePayload: (val) => ({
        renovationProjectId: val ? val.id : null,
        projectPartId: null // Clear project part when project changes
      })
    },

    // Project Part field
    {
      eventMode: 'project-part',
      pickerMode: 'project-part',
      fieldName: 'projectPart',
      titleTemplate: 'Selecteer projectonderdeel voor {count} {entityName}',
      extractCurrentValue: (row) => row.projectPart,
      extractContextValue: (row) => row.renovationProjectId,
      buildUpdatePayload: (val) => ({
        projectPartId: val ? val.id : null
      })
    },

    // Document Date field
    {
      eventMode: 'document-date',
      pickerMode: 'document-date',
      fieldName: 'documentDate',
      titleTemplate: 'Selecteer documentdatum voor {count} {entityName}',
      extractCurrentValue: (row) => row.documentDate,
      buildUpdatePayload: (val) => ({
        documentDate: val || null
      })
    },

    // Expiration Date field
    {
      eventMode: 'expiration-date',
      pickerMode: 'expiration-date',
      fieldName: 'expirationDate',
      titleTemplate: 'Selecteer vervaldatum voor {count} {entityName}',
      extractCurrentValue: (row) => row.expirationDate,
      buildUpdatePayload: (val) => ({
        expirationDate: val || null
      })
    },

    // Summary field
    {
      eventMode: 'summary',
      pickerMode: 'text-input',
      fieldName: 'summary',
      titleTemplate: 'Bewerk samenvatting voor {count} {entityName}',
      extractCurrentValue: (row) => row.summary,
      buildUpdatePayload: (val) => ({
        summary: val || ''
      })
    },

    // Relations field
    {
      eventMode: 'relations',
      pickerMode: 'resource',
      fieldName: 'relations',
      titleTemplate: 'Beheer relaties voor {count} {entityName}',
      extractCurrentValue: (row) => row.relations,
      buildUpdatePayload: (val, currentValue) => {
        const existing = Array.isArray(currentValue) ? currentValue : [];
        // Removal sentinel: picker emits null/undefined → clear all relations
        if (val === null || val === undefined) return { relations: [] };
        // Multi-select case: array of resources comes back directly
        if (Array.isArray(val)) return { relations: val };
        // Single-select case: append new pick to existing, deduped by id+type
        const merged = [...existing, val];
        const deduped = merged.filter(
          (r, i, arr) =>
            arr.findIndex((x) => x.id === r.id && x.type === r.type) === i,
        );
        return { relations: deduped };
      }
    }
  ],

  fieldLabels: {
    status: 'Status',
    building: 'Gebouw',
    unit: 'Unit',
    renovationProject: 'Renovatieproject',
    projectPart: 'Projectonderdeel',
    documentDate: 'Documentdatum',
    expirationDate: 'Vervaldatum',
    summary: 'Samenvatting',
    relations: 'Relaties'
  }
};

// ========== SORT CONFIGURATION ==========

/**
 * Files table sort configuration
 *
 * Defines how to sort by each sortable column.
 */
export const FILES_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'id',
  defaultDirection: 'desc', // Most recent first

  fields: [
    // ID: Sort by file ID
    {
      key: 'id',
      extractor: (row) => Number(row.id) || 0
    },

    // Building: Sort by building ID
    {
      key: 'building',
      extractor: (row) => row.building?.id || ''
    },

    // Unit: Sort by unit ID
    {
      key: 'unit',
      extractor: (row) => row.unit?.id || ''
    },

    // Renovation Project: Sort by project title
    {
      key: 'renovationProject',
      extractor: (row) => row.renovationProject?.title || ''
    },

    // Project Part: Sort by part title
    {
      key: 'projectPart',
      extractor: (row) => row.projectPart?.title || ''
    },

    // Document Date: Sort by document date
    {
      key: 'documentDate',
      extractor: (row) => {
        if (!row.documentDate) return 0;
        return new Date(row.documentDate).getTime();
      }
    },

    // Status: Sort alphabetically
    {
      key: 'status',
      extractor: (row) => row.status || ''
    }
  ],

  caseSensitive: false
};

// ========== COMBINED CONFIGURATION ==========

/**
 * Complete files table configuration
 */
export const FILES_TABLE_CONFIG = {
  columnsConfig: FILES_COLUMNS_CONFIG,
  pickerConfig: FILES_PICKER_CONFIG,
  sortConfig: FILES_SORT_CONFIG
};
